/**
 * Achievements API Endpoints
 * API service for system achievements and collection-based achievements
 */

import { ApiService } from '../apiService'
import type {
  SystemAchievementDto,
  SystemAchievementProgressDto,
  UserSystemAchievementSummaryDto,
  CollectionAchievementProgressSummaryDto,
  UserAchievementProgressDto,
  BadgeDto,
} from '../types/achievements'

// ===================
// SYSTEM ACHIEVEMENTS
// ===================

export const systemAchievementsApi = {
  /** Get all visible system achievements with user's current progress */
  getAll: () =>
    ApiService.get<SystemAchievementDto[]>('/api/system-achievements'),

  /** Get single system achievement details */
  get: (id: string) =>
    ApiService.get<SystemAchievementDto>(`/api/system-achievements/${id}`),

  /** Get user's progress on all system achievements */
  getMyProgress: () =>
    ApiService.get<SystemAchievementProgressDto[]>('/api/system-achievements/my-progress'),

  /** Get only earned system achievements */
  getEarned: () =>
    ApiService.get<SystemAchievementDto[]>('/api/system-achievements/earned'),

  /** Get summary with totals and earned list */
  getSummary: () =>
    ApiService.get<UserSystemAchievementSummaryDto>('/api/system-achievements/summary'),

  /** Get achievements for a specific user (respects privacy settings) */
  getUserAchievements: (userId: string) =>
    ApiService.get<UserAchievementProgressDto[]>(`/api/users/${userId}/achievements`),
}

// ===================
// COLLECTION ACHIEVEMENTS
// ===================

export const collectionAchievementsApi = {
  /** Get user's progress summary for all collection achievements */
  getMyProgressSummary: () =>
    ApiService.get<CollectionAchievementProgressSummaryDto[]>('/api/achievements/my-progress/summary'),

  /** Get completed collection achievement IDs */
  getCompleted: () =>
    ApiService.get<string[]>('/api/achievements/completed'),

  /** Get collection progress for a specific user (respects privacy settings) */
  getUserCollectionProgress: (userId: string) =>
    ApiService.get<CollectionAchievementProgressSummaryDto[]>(`/api/users/${userId}/collection-progress`),
}

// ===================
// BADGES
// ===================

export const badgesApi = {
  /** Get current user's earned badges */
  getMyBadges: () =>
    ApiService.get<BadgeDto[]>('/api/adventure-collections/my-badges'),

  /** Get earned badges for a specific user (respects privacy settings) */
  getUserBadges: (userId: string) =>
    ApiService.get<BadgeDto[]>(`/api/users/${userId}/badges`),
}
